import { useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useT } from '@/i18n/I18nProvider';
import { useCurrentUser } from '@/features/auth/auth.store';
import { chatApi, chatKeys, streamUrl } from '../api/chat.api';
import { CHAT_EVENT_TYPES, handleChatEvent, parseStreamEvent, resyncAll, type StreamContext } from '../lib/stream-handler';
import { useChatStore } from './useChatStore';

const MAX_BACKOFF_MS = 30_000;

/**
 * Jedno połączenie SSE na kartę (`GET /chat/stream`). Zdarzenia trafiają do cache TanStack Query,
 * a stan „pisze…” / połączenia do `useChatStore`. Po zerwaniu łączy się ponownie z narastającym opóźnieniem
 * i odświeża dane. Montowany raz w `AppLayout`, tylko dla użytkowników z `chat.use`.
 */
export function ChatProvider({ children }: { children?: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const t = useT();
  const me = useCurrentUser();
  const meId = me?.id;

  // Najświeższe wartości dla handlera zdarzeń — bez przepinania EventSource przy każdej zmianie trasy / języka.
  const ctxRef = useRef<StreamContext | null>(null);
  ctxRef.current = meId ? { qc, meId, t, navigate: (to, opts) => navigate(to, opts), getPathname: () => window.location.pathname } : null;

  useEffect(() => {
    if (!meId || typeof EventSource === 'undefined') return;
    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let disposed = false;
    let needsResync = false;
    const store = useChatStore.getState();

    const connect = () => {
      if (disposed) return;
      const es = new EventSource(streamUrl(), { withCredentials: true });
      source = es;
      es.onopen = () => {
        attempt = 0;
        store.setConnected(true);
        if (needsResync) {
          needsResync = false;
          resyncAll(qc);
        }
      };
      es.onerror = () => {
        store.setConnected(false);
        needsResync = true;
        // CONNECTING = przeglądarka sama ponawia; CLOSED (np. 401) = robimy to sami.
        if (es.readyState === EventSource.CLOSED) {
          es.close();
          scheduleReconnect();
        }
      };
      for (const type of CHAT_EVENT_TYPES) {
        es.addEventListener(type, (ev) => {
          const event = parseStreamEvent(type, (ev as MessageEvent<string>).data);
          if (event && ctxRef.current) handleChatEvent(event, ctxRef.current);
        });
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt) + Math.random() * 500;
      attempt += 1;
      timer = setTimeout(() => {
        // Zwykłe żądanie odświeża wygasłą sesję (interceptor 401), zanim otworzymy strumień ponownie.
        void chatApi
          .unread()
          .then((summary) => qc.setQueryData(chatKeys.unread, summary))
          .catch(() => undefined)
          .finally(connect);
      }, delay);
    };

    connect();
    return () => {
      disposed = true;
      clearTimeout(timer);
      source?.close();
      store.setConnected(false);
    };
  }, [meId, qc]);

  return <>{children}</>;
}

